import { useState } from "react";
import { Link } from "react-router";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useAuth } from "../lib/auth-context.js";
import { usePreferences } from "../lib/preferences.js";
import {
  archiveAccount,
  deleteLot,
  getAccountHoldings,
  listAccounts,
} from "../lib/api-modules/accounts.js";
import {
  ACCOUNT_TYPE_LABELS,
  LIABILITY_ACCOUNT_TYPES,
  type AccountHoldingDetail,
  type AccountLotDetail,
  type AccountSummary,
  type AccountsListResponse,
  type CostBasisMethod,
  type MembershipRole,
} from "../lib/api-modules/types.js";
import { formatCentsCurrency } from "../lib/util/money.js";
import { ErrorMessage } from "../components/auth/error-message.js";
import { ConfirmButton } from "../components/confirm-button.js";
import { NotesPanel } from "../components/notes-panel.js";
import { DecisionsPanel } from "../components/decisions-panel.js";
import { AccountForm } from "../components/accounts/AccountForm.js";
import { NetWorthBridge } from "../components/accounts/NetWorthBridge.js";
import { LotForm } from "../components/accounts/LotForm.js";
import { SaleForm } from "../components/accounts/SaleForm.js";
import { Ny529DeductionTile } from "../components/accounts/Ny529DeductionTile.js";
import { Plan529ContributionForm } from "../components/accounts/Plan529ContributionForm.js";
import { HsaReceiptsPanel } from "../components/accounts/HsaReceiptsPanel.js";
import { UtmaMilestonePanel } from "../components/accounts/UtmaMilestonePanel.js";
import { SuggestedPromptsTile } from "../components/SuggestedPromptsTile.js";
import { PAGE_PROMPTS } from "../lib/data/page-prompts.js";

/**
 * /accounts — Phase 3 Session 2 promotes from read-only (3.1) to full
 * CRUD on accounts + lots.
 *
 * - Read access for any active member.
 * - Write controls (create / edit / archive / add-lot / edit-lot /
 *   delete-lot) for owner/admin/member/contributor.
 * - Viewer/auditor see the same data with no controls + a banner.
 *
 * Liability accounts (mortgage, HELOC, *_loan, credit_card) flip the
 * G/L sign convention: principal going DOWN renders green. Investment
 * and cash accounts keep the standard "value up = green" convention.
 */

const READ_ONLY_ROLES: ReadonlySet<MembershipRole> = new Set([
  "viewer",
  "auditor",
]);

export function AccountsPage() {
  const { user } = useAuth();
  const { readOnlyMode } = usePreferences();
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(
    null,
  );
  const [creating, setCreating] = useState(false);
  if (!user) return null;

  // Two-layer gate: backend role gate + UX read-only toggle. Backend
  // is the source of truth; this hides write affordances when the user
  // toggled read-only on (Phase 4 Session 1 — the dashboard exposes
  // the toggle).
  const canWrite = !READ_ONLY_ROLES.has(user.role) && !readOnlyMode;

  return (
    <div className="mx-auto max-w-5xl p-6">
      <header className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Accounts</h1>
          <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
            Your household's investment, cash, and liability accounts.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {canWrite && (
            <Link
              to="/import"
              className="text-sm font-medium text-brand-600 hover:underline dark:text-brand-400"
            >
              Import files
            </Link>
          )}
          {canWrite && !creating && (
            <button
              type="button"
              onClick={() => setCreating(true)}
              className="rounded-md bg-brand-500 px-3 py-1.5 text-sm font-medium text-white shadow-sm transition hover:bg-brand-600"
            >
              New account
            </button>
          )}
        </div>
      </header>

      {!canWrite && (
        <p className="mb-6 rounded-md border border-zinc-200 bg-zinc-50 p-3 text-sm text-zinc-600 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-400">
          {READ_ONLY_ROLES.has(user.role)
            ? "Read-only access — ask an owner or admin to make changes."
            : "Read-only mode is on — toggle it off in the dashboard to make changes."}
        </p>
      )}

      {creating && (
        <div className="mb-6">
          <AccountForm mode="create" onClose={() => setCreating(false)} />
        </div>
      )}

      <AccountsList
        canWrite={canWrite}
        selectedAccountId={selectedAccountId}
        onSelect={setSelectedAccountId}
      />

      {selectedAccountId && (
        <AccountDrilldown
          accountId={selectedAccountId}
          canWrite={canWrite}
          onClearSelection={() => setSelectedAccountId(null)}
        />
      )}
      <div className="mt-8">
        <SuggestedPromptsTile prompts={PAGE_PROMPTS.accounts} />
      </div>
    </div>
  );
}

// === List ==============================================================

function AccountsList({
  canWrite,
  selectedAccountId,
  onSelect,
}: {
  canWrite: boolean;
  selectedAccountId: string | null;
  onSelect: (id: string | null) => void;
}) {
  const { data, isPending, isError, error } = useQuery({
    queryKey: ["accounts"],
    queryFn: listAccounts,
  });

  return (
    <section aria-labelledby="accounts-heading" className="mt-2">
      <h2 id="accounts-heading" className="mb-3 text-lg font-medium">
        Your accounts
      </h2>
      {isPending && <p className="text-sm text-zinc-500">Loading…</p>}
      {isError && (
        <ErrorMessage>
          Couldn't load accounts:{" "}
          {error instanceof Error ? error.message : "unknown"}
        </ErrorMessage>
      )}
      {data && data.accounts.length === 0 && (
        <p className="text-sm text-zinc-500">
          No accounts yet — click <span className="font-medium">New account</span>{" "}
          to add one manually, or drop a Merrill CSV at
          <span className="mx-1 font-mono text-xs">
            s3://hearth-dev-uploads/merrill-csv/&lt;household&gt;/&lt;user&gt;/
          </span>
          to import.
        </p>
      )}
      {data && data.accounts.length > 0 && (
        <>
          <ul className="grid gap-3 sm:grid-cols-2">
            {data.accounts.map((acct) => (
              <AccountCard
                key={acct.account_id}
                account={acct}
                canWrite={canWrite}
                isSelected={acct.account_id === selectedAccountId}
                onSelect={onSelect}
              />
            ))}
          </ul>
          <HouseholdTotals totals={data.totals} />
        </>
      )}
    </section>
  );
}

function AccountCard({
  account,
  canWrite,
  isSelected,
  onSelect,
}: {
  account: AccountSummary;
  canWrite: boolean;
  isSelected: boolean;
  onSelect: (id: string | null) => void;
}) {
  const [editing, setEditing] = useState(false);
  const isLiability = LIABILITY_ACCOUNT_TYPES.has(account.account_type);

  // Liability G/L flip: when this is debt, "delta" is current_principal
  // minus originating_principal (negative = paid down some, which is
  // good). For asset accounts, fall back to the unrealized G/L.
  const delta = isLiability
    ? (account.current_principal_cents ?? 0) -
      (account.originating_principal_cents ?? 0)
    : account.unrealized_gain_loss_cents;
  const deltaIsGood = isLiability ? delta <= 0 : delta >= 0;
  const deltaClass = deltaIsGood
    ? "text-emerald-700 dark:text-emerald-300"
    : "text-rose-700 dark:text-rose-300";
  const displayValue = isLiability
    ? -(account.current_principal_cents ?? 0)
    : account.total_value_cents;

  if (editing) {
    return (
      <li className="rounded-md border border-zinc-200 dark:border-zinc-800 sm:col-span-2">
        <div className="p-4">
          <AccountForm
            mode="edit"
            account={account}
            onClose={() => setEditing(false)}
          />
        </div>
      </li>
    );
  }

  return (
    <li>
      <div
        className={`relative w-full rounded-md border p-4 text-left transition ${
          isSelected
            ? "border-brand-500 bg-brand-50/40 dark:border-brand-400 dark:bg-brand-950/40"
            : "border-zinc-200 hover:border-zinc-300 dark:border-zinc-800 dark:hover:border-zinc-700"
        }`}
      >
        {canWrite && (
          <div className="absolute right-3 top-3 flex items-center gap-1">
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="rounded-md border border-zinc-300 px-2 py-0.5 text-xs hover:bg-zinc-50 dark:border-zinc-700 dark:hover:bg-zinc-900"
              aria-label={`Edit ${account.name}`}
            >
              Edit
            </button>
            <ArchiveButton account={account} />
          </div>
        )}
        <button
          type="button"
          onClick={() =>
            onSelect(isSelected ? null : account.account_id)
          }
          aria-pressed={isSelected}
          className="mt-0 block w-full pr-16 text-left"
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium">{account.name}</h3>
              <p className="text-xs uppercase tracking-wide text-zinc-500">
                {ACCOUNT_TYPE_LABELS[account.account_type] ??
                  account.account_type}
                {" · "}
                {account.institution}
              </p>
            </div>
            <span className="text-xs text-zinc-500">
              {account.position_count}{" "}
              {account.position_count === 1 ? "position" : "positions"}
            </span>
          </div>
          <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
            <dt className="text-zinc-500">
              {isLiability ? "Net (debt)" : "Total value"}
            </dt>
            <dd className="text-right font-medium">
              {formatCentsCurrency(displayValue)}
            </dd>
            {!isLiability && (
              <>
                <dt className="text-zinc-500">Cost basis</dt>
                <dd className="text-right">
                  {formatCentsCurrency(account.total_cost_basis_cents)}
                </dd>
                <dt className="text-zinc-500">Unrealized G/L</dt>
                <dd className={`text-right font-medium ${deltaClass}`}>
                  {delta >= 0 ? "+" : ""}
                  {formatCentsCurrency(delta)}
                </dd>
              </>
            )}
            {isLiability && (
              <>
                <dt className="text-zinc-500">Originating</dt>
                <dd className="text-right">
                  {formatCentsCurrency(
                    account.originating_principal_cents ?? 0,
                  )}
                </dd>
                <dt className="text-zinc-500">Paid down</dt>
                <dd className={`text-right font-medium ${deltaClass}`}>
                  {delta < 0 ? "−" : "+"}
                  {formatCentsCurrency(Math.abs(delta))}
                </dd>
              </>
            )}
            {account.cash_balance_cents !== 0 && (
              <>
                <dt className="text-zinc-500">Cash</dt>
                <dd className="text-right">
                  {formatCentsCurrency(account.cash_balance_cents)}
                </dd>
              </>
            )}
            {account.margin_balance_cents !== null &&
              account.margin_balance_cents !== 0 && (
                <>
                  <dt className="text-zinc-500">Margin</dt>
                  <dd className="text-right text-rose-700 dark:text-rose-300">
                    {formatCentsCurrency(account.margin_balance_cents)}
                  </dd>
                </>
              )}
          </dl>
          {account.as_of_date && (
            <p className="mt-2 text-xs text-zinc-500">
              As of {account.as_of_date}
            </p>
          )}
          {account.source && account.source !== "manual" && (
            <p className="mt-1 text-xs text-zinc-500">
              Source: {account.source}
            </p>
          )}
        </button>
      </div>
    </li>
  );
}

function ArchiveButton({ account }: { account: AccountSummary }) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => archiveAccount(account.account_id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["accounts"] });
    },
  });
  return (
    <ConfirmButton
      label="Archive"
      ariaLabel={`Archive ${account.name}`}
      size="xs"
      onConfirm={() => mutation.mutate()}
      isPending={mutation.isPending}
    />
  );
}

function HouseholdTotals({
  totals,
}: {
  totals: AccountsListResponse["totals"];
}) {
  const gainLossClass =
    totals.unrealized_gain_loss_cents >= 0
      ? "text-emerald-700 dark:text-emerald-300"
      : "text-rose-700 dark:text-rose-300";
  return (
    <section
      aria-label="Household totals"
      className="mt-6 rounded-md border border-zinc-200 bg-zinc-50/40 p-4 dark:border-zinc-800 dark:bg-zinc-900/40"
    >
      <h3 className="text-sm font-medium uppercase tracking-wide text-zinc-500">
        Household total
      </h3>
      <p className="mt-1 text-xs text-zinc-500">
        Everything you own minus what you owe, the same as your Dashboard.
      </p>
      <div className="mt-3">
        <NetWorthBridge totals={totals} />
      </div>
      <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-sm sm:grid-cols-4">
        <div>
          <dt className="text-xs text-zinc-500">Cost basis</dt>
          <dd>{formatCentsCurrency(totals.total_cost_basis_cents)}</dd>
        </div>
        <div>
          <dt className="text-xs text-zinc-500">Unrealized G/L</dt>
          <dd className={`font-medium ${gainLossClass}`}>
            {totals.unrealized_gain_loss_cents >= 0 ? "+" : ""}
            {formatCentsCurrency(totals.unrealized_gain_loss_cents)}
          </dd>
        </div>
      </dl>
    </section>
  );
}

// === Drilldown =========================================================

function AccountDrilldown({
  accountId,
  canWrite,
  onClearSelection,
}: {
  accountId: string;
  canWrite: boolean;
  onClearSelection: () => void;
}) {
  const { data, isPending, isError, error } = useQuery({
    queryKey: ["accounts", accountId, "holdings"],
    queryFn: () => getAccountHoldings(accountId),
  });

  return (
    <section aria-labelledby="drill-heading" className="mt-8">
      <div className="mb-3 flex items-center justify-between">
        <h2 id="drill-heading" className="text-lg font-medium">
          {data?.account.name ?? "Account"} — holdings
        </h2>
        <button
          type="button"
          onClick={onClearSelection}
          className="text-sm text-zinc-500 hover:underline"
        >
          Close
        </button>
      </div>
      {isPending && <p className="text-sm text-zinc-500">Loading…</p>}
      {isError && (
        <ErrorMessage>
          Couldn't load holdings:{" "}
          {error instanceof Error ? error.message : "unknown"}
        </ErrorMessage>
      )}
      {data && data.holdings.length === 0 && (
        <p className="text-sm text-zinc-500">No positions in this account.</p>
      )}
      {data && data.holdings.length > 0 && (
        <ul className="space-y-3">
          {data.holdings.map((h) => (
            <HoldingRow
              key={`${h.account_id}#${h.ticker}`}
              accountId={accountId}
              holding={h}
              canWrite={canWrite}
              costBasisMethod={data.account.cost_basis_method}
            />
          ))}
        </ul>
      )}
      {data && canWrite && (
        <div className="mt-4">
          <AddNewLotControl accountId={accountId} />
        </div>
      )}
      {data && (
        <>
          {/* Phase 5 Session 5 — account-type-aware conditional sections. */}
          {(data.account.account_type === "529" ||
            data.account.account_type === "529_prepaid") && (
            <>
              {data.account.plan_state_code === "NY" && (
                <Ny529DeductionTile accountId={accountId} />
              )}
              {canWrite && (
                <section className="mt-4 rounded-md border border-zinc-200 p-4 dark:border-zinc-800">
                  <h3 className="mb-2 text-base font-semibold">
                    Record a 529 contribution
                  </h3>
                  <Plan529ContributionForm accountId={accountId} />
                </section>
              )}
            </>
          )}
          {data.account.account_type === "hsa" && (
            <HsaReceiptsPanel
              accountId={accountId}
              canWrite={canWrite}
            />
          )}
          {data.account.account_type === "utma" && (
            <UtmaMilestonePanel account={data.account} />
          )}
          <NotesPanel
            entityType="account"
            entityId={accountId}
            canWriteOverride={canWrite}
          />
          <DecisionsPanel
            entityType="account"
            entityId={accountId}
            canWriteOverride={canWrite}
          />
        </>
      )}
    </section>
  );
}

function AddNewLotControl({ accountId }: { accountId: string }) {
  const [open, setOpen] = useState(false);
  if (open) {
    return (
      <LotForm
        mode="create"
        accountId={accountId}
        onClose={() => setOpen(false)}
      />
    );
  }
  return (
    <button
      type="button"
      onClick={() => setOpen(true)}
      className="rounded-md border border-dashed border-zinc-300 px-3 py-1.5 text-sm text-zinc-600 hover:bg-zinc-50 dark:border-zinc-700 dark:text-zinc-400 dark:hover:bg-zinc-900"
    >
      + Add lot for a new ticker
    </button>
  );
}

function HoldingRow({
  accountId,
  holding,
  canWrite,
  costBasisMethod,
}: {
  accountId: string;
  holding: AccountHoldingDetail;
  canWrite: boolean;
  costBasisMethod: CostBasisMethod | null;
}) {
  const [expanded, setExpanded] = useState(false);
  const [addingLot, setAddingLot] = useState(false);
  const [selling, setSelling] = useState(false);
  const gainLoss = holding.unrealized_gain_loss_cents ?? 0;
  const gainLossClass =
    gainLoss >= 0
      ? "text-emerald-700 dark:text-emerald-300"
      : "text-rose-700 dark:text-rose-300";
  return (
    <li className="rounded-md border border-zinc-200 dark:border-zinc-800">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className="grid w-full grid-cols-2 items-center gap-x-4 p-4 text-left sm:grid-cols-6"
      >
        <div className="sm:col-span-2">
          <Link
            to={`/chart/${encodeURIComponent(holding.ticker)}`}
            onClick={(e) => e.stopPropagation()}
            className="font-medium text-brand-600 hover:underline dark:text-brand-400"
          >
            {holding.ticker}
          </Link>
          <p className="text-xs text-zinc-500">
            {holding.lots.length}{" "}
            {holding.lots.length === 1 ? "lot" : "lots"}
          </p>
        </div>
        <div className="text-right text-sm">
          <p className="text-xs text-zinc-500">Quantity</p>
          <p className="font-medium tabular-nums">
            {holding.quantity.toLocaleString("en-US", {
              maximumFractionDigits: 4,
            })}
          </p>
        </div>
        <div className="text-right text-sm">
          <p className="text-xs text-zinc-500">Cost basis</p>
          <p className="tabular-nums">
            {holding.cost_basis_cents !== null
              ? formatCentsCurrency(holding.cost_basis_cents)
              : "—"}
          </p>
        </div>
        <div className="text-right text-sm">
          <p className="text-xs text-zinc-500">Value</p>
          <p className="tabular-nums">
            {holding.value_cents !== null
              ? formatCentsCurrency(holding.value_cents)
              : "—"}
          </p>
        </div>
        <div className={`text-right text-sm font-medium ${gainLossClass}`}>
          <p className="text-xs text-zinc-500">G/L</p>
          <p className="tabular-nums">
            {holding.unrealized_gain_loss_cents !== null
              ? `${gainLoss >= 0 ? "+" : ""}${formatCentsCurrency(gainLoss)}`
              : "—"}
          </p>
        </div>
      </button>
      {expanded && (
        <div className="border-t border-zinc-200 dark:border-zinc-800">
          {holding.lots.length > 0 && (
            <LotTable
              accountId={accountId}
              ticker={holding.ticker}
              lots={holding.lots}
              canWrite={canWrite}
            />
          )}
          {canWrite && !addingLot && !selling && (
            <div className="flex flex-wrap gap-2 px-4 py-3">
              <button
                type="button"
                onClick={() => setAddingLot(true)}
                className="rounded-md border border-zinc-300 px-2 py-1 text-xs hover:bg-zinc-50 dark:border-zinc-700 dark:hover:bg-zinc-900"
              >
                + Add lot to {holding.ticker}
              </button>
              {holding.quantity > 0 && (
                <button
                  type="button"
                  onClick={() => setSelling(true)}
                  className="rounded-md border border-rose-300 px-2 py-1 text-xs text-rose-700 hover:bg-rose-50 dark:border-rose-700/60 dark:text-rose-300 dark:hover:bg-rose-950/40"
                >
                  Sell {holding.ticker}
                </button>
              )}
            </div>
          )}
          {addingLot && (
            <div className="px-4 py-3">
              <LotForm
                mode="create"
                accountId={accountId}
                tickerHint={holding.ticker}
                onClose={() => setAddingLot(false)}
              />
            </div>
          )}
          {selling && (
            <div className="px-4 py-3">
              <SaleForm
                accountId={accountId}
                ticker={holding.ticker}
                availableQuantity={holding.quantity}
                lots={holding.lots}
                costBasisMethod={costBasisMethod}
                onClose={() => setSelling(false)}
              />
            </div>
          )}
        </div>
      )}
    </li>
  );
}

function LotTable({
  accountId,
  ticker,
  lots,
  canWrite,
}: {
  accountId: string;
  ticker: string;
  lots: readonly AccountLotDetail[];
  canWrite: boolean;
}) {
  return (
    <div className="px-4 py-3">
      <table className="w-full text-sm">
        <thead className="text-left text-xs uppercase tracking-wide text-zinc-500">
          <tr>
            <th scope="col" className="py-1 font-medium">Acquired</th>
            <th scope="col" className="py-1 text-right font-medium">Qty</th>
            <th scope="col" className="py-1 text-right font-medium">Unit cost</th>
            <th scope="col" className="py-1 text-right font-medium">Cost basis</th>
            <th scope="col" className="py-1 text-right font-medium">Term</th>
            <th scope="col" className="py-1 text-right font-medium">Source</th>
            {canWrite && <th scope="col" className="py-1 text-right font-medium" />}
          </tr>
        </thead>
        <tbody>
          {lots.map((lot) => (
            <LotRow
              key={lot.lot_id}
              accountId={accountId}
              ticker={ticker}
              lot={lot}
              canWrite={canWrite}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LotRow({
  accountId,
  ticker,
  lot,
  canWrite,
}: {
  accountId: string;
  ticker: string;
  lot: AccountLotDetail;
  canWrite: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const isManual = lot.source === "manual" || lot.source === null;

  if (editing) {
    return (
      <tr>
        <td colSpan={7}>
          <div className="py-2">
            <LotForm
              mode="edit"
              accountId={accountId}
              lot={{ ...lot, ticker }}
              onClose={() => setEditing(false)}
            />
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr className="border-t border-zinc-100 last:border-0 dark:border-zinc-900">
      <td className="py-1.5">{lot.purchase_date}</td>
      <td className="py-1.5 text-right tabular-nums">
        {lot.quantity.toLocaleString("en-US", {
          maximumFractionDigits: 4,
        })}
      </td>
      <td className="py-1.5 text-right tabular-nums">
        {lot.unit_cost_cents !== null
          ? formatCentsCurrency(lot.unit_cost_cents)
          : "—"}
      </td>
      <td className="py-1.5 text-right tabular-nums">
        {formatCentsCurrency(lot.cost_basis_cents)}
      </td>
      <td className="py-1.5 text-right text-xs uppercase text-zinc-500">
        {lot.tax_term ?? "—"}
      </td>
      <td className="py-1.5 text-right text-xs text-zinc-500">
        {lot.source ?? "—"}
      </td>
      {canWrite && (
        <td className="py-1.5 text-right">
          {isManual ? (
            <span className="inline-flex items-center gap-1">
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="rounded-md border border-zinc-300 px-1.5 py-0.5 text-xs hover:bg-zinc-50 dark:border-zinc-700 dark:hover:bg-zinc-900"
              >
                Edit
              </button>
              <DeleteLotButton
                accountId={accountId}
                lotId={lot.lot_id}
              />
            </span>
          ) : (
            <span
              className="text-xs text-zinc-400"
              title="Imported from broker — re-import to update"
            >
              read-only
            </span>
          )}
        </td>
      )}
    </tr>
  );
}

function DeleteLotButton({
  accountId,
  lotId,
}: {
  accountId: string;
  lotId: string;
}) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => deleteLot(accountId, lotId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["accounts"] });
      // Holdings refetch delayed because recompute now runs via the
      // lots-table DDB stream (Phase 3.4); 800ms covers median streams
      // replay tail.
      setTimeout(() => {
        void queryClient.invalidateQueries({
          queryKey: ["accounts", accountId, "holdings"],
        });
      }, 800);
    },
  });
  return (
    <ConfirmButton
      label="Delete"
      size="xs"
      onConfirm={() => mutation.mutate()}
      isPending={mutation.isPending}
    />
  );
}
